import { Controller } from "@hotwired/stimulus";
import StimulusReflex from "stimulus_reflex";

export default class extends Controller {
  static targets = [
    "input",
    "results",
    "selectedId",
    "selectedIds",
    "selected",
    "spinner",
  ];
  static values = {
    selectedId: String,
    selectedIds: Array,
  };

  connect() {
    StimulusReflex.register(this);
    console.log("AuthorityFormSearchController connected");

    // Check if this is single or multiple selection mode
    this.isMultipleSelection = this.hasSelectedIdsTarget;

    if (this.isMultipleSelection) {
      this.selectedAuthorities = new Set();
      // Initialize with existing selected authorities
      this.selectedIdsTarget
        .querySelectorAll('input[type="hidden"]')
        .forEach((input) => {
          this.selectedAuthorities.add(input.value);
        });
    }
  }

  search() {
    const query = this.inputTarget.value.trim();
    if (query.length < 2) {
      this.resultsTarget.classList.add("hidden");
      return;
    }

    // Set the query in the dataset for the reflex to access
    this.element.dataset.query = query;

    // Show spinner and results container
    this.showSpinner();
    this.resultsTarget.classList.remove("hidden");
    this.resultsTarget.classList.add("opacity-50");

    // Trigger the reflex
    this.stimulate("AuthorityFormSearch#search");
  }

  // Reflex lifecycle methods
  beforeSearch() {
    this.resultsTarget.classList.add("opacity-50");
    this.showSpinner();
  }

  searchSuccess() {
    this.resultsTarget.classList.remove("opacity-50");
    this.hideSpinner();
  }

  searchError(error) {
    console.error("Search error:", error);
    this.resultsTarget.classList.remove("opacity-50");
    this.resultsTarget.classList.add("hidden");
  }

  select(event) {
    const authorityId = event.currentTarget.dataset.authorityId;
    const authorityName = event.currentTarget.dataset.authorityName;

    if (this.isMultipleSelection) {
      // Multiple selection mode (for organization forms)
      this.selectedAuthorities.add(authorityId);
      this.updateHiddenInputs();
      this.addSelectedAuthority(authorityId, authorityName);
      this.inputTarget.value = "";
      this.resultsTarget.classList.add("hidden");
      this.selectedIdsTarget.dispatchEvent(new Event("change"));
    } else {
      // Single selection mode (for appellation forms)
      this.selectedIdTarget.value = authorityId;
      this.inputTarget.value = authorityName;
      this.resultsTarget.classList.add("hidden");

      // Trigger change event for form validation
      this.inputTarget.dispatchEvent(new Event("change"));
      this.selectedIdTarget.dispatchEvent(new Event("change"));
    }
  }

  removeAuthority(event) {
    if (!this.isMultipleSelection) return;

    const authorityId = event.currentTarget.dataset.authorityId;

    // Remove from selected set
    this.selectedAuthorities.delete(authorityId);

    // Update hidden input
    this.updateHiddenInputs();

    // Remove from display
    event.currentTarget.closest("[data-authority-item]").remove();

    // Trigger change event for form validation
    this.selectedIdsTarget.dispatchEvent(new Event("change"));
  }

  updateHiddenInputs() {
    if (!this.isMultipleSelection) return;

    // Clear existing hidden inputs
    this.selectedIdsTarget.innerHTML = "";

    // Add new hidden inputs for each selected authority
    this.selectedAuthorities.forEach((authorityId) => {
      const hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = "organization_form[authority_ids][]";
      hiddenInput.value = authorityId;
      this.selectedIdsTarget.appendChild(hiddenInput);
    });
  }

  addSelectedAuthority(authorityId, authorityName) {
    if (!this.isMultipleSelection) return;

    const selectedItem = document.createElement("div");
    selectedItem.className =
      "flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2";
    selectedItem.setAttribute("data-authority-item", authorityId);

    selectedItem.innerHTML = `
      <span class="text-sm font-medium text-indigo-900">${authorityName}</span>
      <button type="button" 
              class="text-indigo-600 hover:text-indigo-800 ml-2 transition-colors duration-200"
              data-action="click->authority-form-search#removeAuthority"
              data-authority-id="${authorityId}">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `;

    this.selectedTarget.appendChild(selectedItem);
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden");
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden");
    }
  }

  // Called by the reflex after search
  receiveResults(html) {
    this.resultsTarget.innerHTML = html;
    this.resultsTarget.classList.remove("hidden");
  }
}
